import React, { useMemo, useState } from 'react'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'

const { FiBookOpen, FiDownloadCloud, FiPlus, FiSettings } = FiIcons

const Gradebook = () => {
  const [selectedStudent, setSelectedStudent] = useState('Ava M.')
  const [selectedSubject, setSelectedSubject] = useState('Mathematics')
  const [gradeWeights, setGradeWeights] = useState({
    homework: 30,
    quizzes: 30,
    projects: 20,
    exams: 20
  })
  const [entries, setEntries] = useState([
    { id: 1, type: 'Quiz', title: 'Fractions Quiz', score: 18, total: 20, weight: 'quizzes', subject: 'Mathematics' },
    { id: 2, type: 'Homework', title: 'Algebra Practice', score: 10, total: 10, weight: 'homework', subject: 'Mathematics' },
    { id: 3, type: 'Project', title: 'Science Lab Report', score: 45, total: 50, weight: 'projects', subject: 'Science' },
  ])
  const [newEntry, setNewEntry] = useState({ title: '', type: 'Homework', score: '', total: '', weight: 'homework' })

  const filteredEntries = entries.filter(entry => entry.subject === selectedSubject)

  const weightedAverage = useMemo(() => {
    const categories = filteredEntries.reduce((acc, entry) => {
      const pct = entry.total ? entry.score / entry.total : 0
      acc[entry.weight] = acc[entry.weight] || []
      acc[entry.weight].push(pct)
      return acc
    }, {})

    let total = 0
    let weightSum = 0
    Object.entries(categories).forEach(([weightKey, scores]) => {
      const averageScore = scores.reduce((s, v) => s + v, 0) / scores.length
      const weightPct = gradeWeights[weightKey] || 0
      total += averageScore * weightPct
      weightSum += weightPct
    })

    return weightSum ? Math.round((total / weightSum) * 100) : 0
  }, [filteredEntries, gradeWeights])

  const addEntry = () => {
    if (!newEntry.title || !newEntry.score || !newEntry.total) return
    setEntries(prev => [
      ...prev,
      {
        id: prev.length + 1,
        ...newEntry,
        score: Number(newEntry.score),
        total: Number(newEntry.total),
        subject: selectedSubject
      }
    ])
    setNewEntry({ title: '', type: 'Homework', score: '', total: '', weight: 'homework' })
  }

  const exportCsv = () => {
    const header = 'Student,Subject,Title,Type,Score,Total,Weight\n'
    const rows = filteredEntries.map(entry =>
      [selectedStudent, selectedSubject, entry.title, entry.type, entry.score, entry.total, entry.weight].join(',')
    ).join('\n')
    const blob = new Blob([header + rows], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `${selectedStudent}-${selectedSubject}-gradebook.csv`
    link.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-[#00AAFF] to-[#1B4C87] rounded-2xl p-8 text-white shadow-lg">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm uppercase tracking-wider text-blue-100">Records</p>
            <h1 className="text-3xl font-bold">Gradebook</h1>
            <p className="text-blue-100 mt-2">Track grades, weight categories, and export official records.</p>
          </div>
          <SafeIcon icon={FiBookOpen} className="w-12 h-12 text-white opacity-80" />
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-md p-6 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <p className="text-sm text-gray-600">Student</p>
            <select
              value={selectedStudent}
              onChange={(e) => setSelectedStudent(e.target.value)}
              className="mt-1 w-full border border-gray-200 rounded-lg px-4 py-2 focus:ring-2 focus:ring-[#00AAFF] focus:border-transparent"
            >
              <option value="Ava M.">Ava M.</option>
              <option value="Liam J.">Liam J.</option>
              <option value="Sofia R.">Sofia R.</option>
            </select>
          </div>
          <div>
            <p className="text-sm text-gray-600">Subject</p>
            <select
              value={selectedSubject}
              onChange={(e) => setSelectedSubject(e.target.value)}
              className="mt-1 w-full border border-gray-200 rounded-lg px-4 py-2 focus:ring-2 focus:ring-[#00AAFF] focus:border-transparent"
            >
              <option value="Mathematics">Mathematics</option>
              <option value="Science">Science</option>
              <option value="Language Arts">Language Arts</option>
            </select>
          </div>
          <div className="flex items-end justify-end">
            <button
              onClick={exportCsv}
              className="inline-flex items-center px-4 py-2 bg-[#1B4C87] text-white rounded-lg hover:bg-[#163b68] transition-colors"
            >
              <SafeIcon icon={FiDownloadCloud} className="w-4 h-4 mr-2" />
              Export CSV
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {Object.entries(gradeWeights).map(([key, value]) => (
            <div key={key} className="p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 capitalize">{key}</p>
                  <p className="text-2xl font-semibold text-[#1B4C87]">{value}%</p>
                </div>
                <input
                  type="number"
                  value={value}
                  min="0"
                  max="100"
                  onChange={(e) => setGradeWeights({ ...gradeWeights, [key]: Number(e.target.value) })}
                  className="w-16 border border-gray-200 rounded-lg px-2 py-1 text-right focus:ring-2 focus:ring-[#00AAFF] focus:border-transparent"
                />
              </div>
            </div>
          ))}
        </div>

        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-600">Weighted Average</p>
            <p className="text-3xl font-bold text-[#00AAFF]">{weightedAverage}%</p>
          </div>
          <div className="flex items-center text-sm text-gray-500">
            <SafeIcon icon={FiSettings} className="w-4 h-4 mr-2" />
            Averages recalculate automatically when grades are updated.
          </div>
        </div>

        <div className="border-t border-gray-100 pt-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-semibold text-gray-800">Grade Entries</h3>
            <div className="flex items-center space-x-2">
              <input
                type="text"
                placeholder="Title"
                value={newEntry.title}
                onChange={(e) => setNewEntry({ ...newEntry, title: e.target.value })}
                className="border border-gray-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#00AAFF] focus:border-transparent"
              />
              <input
                type="number"
                placeholder="Score"
                value={newEntry.score}
                onChange={(e) => setNewEntry({ ...newEntry, score: e.target.value })}
                className="w-24 border border-gray-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#00AAFF] focus:border-transparent"
              />
              <input
                type="number"
                placeholder="Total"
                value={newEntry.total}
                onChange={(e) => setNewEntry({ ...newEntry, total: e.target.value })}
                className="w-24 border border-gray-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#00AAFF] focus:border-transparent"
              />
              <select
                value={newEntry.weight}
                onChange={(e) => setNewEntry({ ...newEntry, weight: e.target.value })}
                className="border border-gray-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#00AAFF] focus:border-transparent"
              >
                <option value="homework">Homework</option>
                <option value="quizzes">Quizzes</option>
                <option value="projects">Projects</option>
                <option value="exams">Exams</option>
              </select>
              <button
                onClick={addEntry}
                className="inline-flex items-center px-3 py-2 bg-[#00AAFF] text-white rounded-lg hover:bg-[#0088cc]"
              >
                <SafeIcon icon={FiPlus} className="w-4 h-4 mr-1" />
                Add
              </button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Score</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Weight</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredEntries.map(entry => (
                  <tr key={entry.id}>
                    <td className="px-4 py-3 text-sm text-gray-800">{entry.title}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{entry.type}</td>
                    <td className="px-4 py-3 text-sm text-gray-800 font-semibold">{entry.score}/{entry.total}</td>
                    <td className="px-4 py-3 text-sm text-gray-600 capitalize">{entry.weight}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Gradebook
